import {
  insuranceResultStep,
  type InsuranceCheckInputs,
  type InsuranceResult,
  type InsuranceError,
  type InsuranceEvent
} from "./insuranceTypes"
import type {
  BookedAppointment,
  BookingPaymentOptions,
  BookingPayment,
  ScreeningAppointmentType,
  ScreeningProgram
} from "./hooks"
import type { BookingRecovery } from "@internal/constants"
import { and, assign, setup } from "xstate"

// Payment method applies to this booking, not the member.
export type BookingPaymentMethod = "self_pay" | "insurance"

// The member's selections on the "Select your preferences" screen — the chosen offering and
// location that key the availability lookup. `offeringId` is the carrot-clinic Offering id;
// `region` is a US state code.
export type BookingPreferences = { offeringId: string; region: string }

// The slot the member picked from availability. Mirrors the fields of the carrot-clinic
// `Slot` the later booking call needs (the opaque `slotToken` to redeem, and a `hostId`
// chosen from `availableHostIds`); `startTime` is UTC ISO. Carried now so the booking
// ticket doesn't have to reshape the flow.
export type SelectedSlot = {
  slotToken: string
  startTime: string
  offeringId: string
  availableHostIds: string[]
}

export type BookingFlowInput = {
  insuranceEntry?: boolean
  alreadySignedConsent: boolean
  program: ScreeningProgram
  // Resolved on flow load (initial vs follow-up). Keys the screening question set alongside program.
  appointmentType: ScreeningAppointmentType
  // Known-data eligibility gate (MEM-760): the not-eligible message id from beginBooking, or null
  // when eligible. Non-null routes the flow straight to notEligible at init, before consent, and the
  // id is what the not-eligible page renders (one message defined today).
  knownDataExclusionMessageId: string | null
  // Whether this offering has any member-facing screening questions. False → skip the health-questions
  // step entirely (a follow-up has none), so the member never sees an empty screen.
  hasScreeningQuestions: boolean
}
export type BookingFlowContext = {
  insuranceInputs: InsuranceCheckInputs | null
  insuranceResult: InsuranceResult | null
  insuranceError: InsuranceError | null
  insuranceRequestId: string | null
  paymentOptions: BookingPaymentOptions | null
  insuranceEntry: boolean
  alreadySignedConsent: boolean
  program: ScreeningProgram
  appointmentType: ScreeningAppointmentType
  hasScreeningQuestions: boolean
  // Valid only for this actor's program/appointment type; insurance location changes do not reset it.
  hasPassedScreening: boolean
  // The exclusion message id the not-eligible page renders. Seeded from the known-data gate at init
  // and overwritten by a screening DISQUALIFIED; null until/unless the member is turned away.
  exclusionMessageId: string | null
  // True only when a screening answer disqualified the member (not the known-data gate). Gates the
  // "back to questions" affordance so a known-data refusal can't be re-answered to bypass the gate.
  disqualifiedByScreener: boolean
  preferences: BookingPreferences | null
  selectedSlot: SelectedSlot | null
  // The booking carrot-clinic minted for this attempt: retries and slot changes reuse it; an
  // abandoned booking, a cancelled payment or a new actor does not.
  bookingId: string | null
  bookingOfferingId: string | null
  paymentPreauthorized: boolean
  payment: BookingPayment | null
  paymentMethod: BookingPaymentMethod | null
  // The appointment returned by the booking call, shown on the confirmation screen.
  appointment: BookedAppointment | null
  recovery: BookingRecovery | null
  paymentFormRemountKey: number
}

export type BookingFlowEvent =
  | { type: "CONTINUE" }
  | { type: "PAYMENT_OPTIONS_LOADED"; paymentOptions: BookingPaymentOptions | null }
  | { type: "INSURANCE_BACK" }
  | { type: "BACK_TO_INSURANCE" }
  | { type: "PAYMENT_REQUIREMENT_LOADED"; requiresPayment: boolean }
  | InsuranceEvent
  | { type: "PREPARE_BOOKING"; offeringId: string; bookingId: string }
  | { type: "CONTINUE_TO_PAYMENT"; preferences: BookingPreferences; slot: SelectedSlot; payment: BookingPayment }
  | { type: "BACK_TO_AVAILABILITY" }
  | { type: "PAYMENT_CANCELED" }
  | { type: "BOOKING_ABANDONED" }
  | {
      type: "BOOKING_RECOVERY"
      recovery: BookingRecovery
      preferences?: BookingPreferences
      slot?: SelectedSlot
      payment?: BookingPayment
    }
  | { type: "PAYMENT_PREAUTHORIZED" }
  // `fromScreener` distinguishes a screening-answer refusal (true → "back to questions" is offered,
  // re-answering makes sense) from a booking-time known-data refusal (false → terminal; re-answering
  // the screener can't change a known-data verdict, and it must not reopen the gate).
  | { type: "DISQUALIFIED"; exclusionMessageId: string; fromScreener: boolean }
  | { type: "BACK_TO_QUESTIONS" }
  | { type: "NEEDS_CONSENT" }
  // The booking call has already succeeded by the time this fires (the preferences screen owns
  // it), so the appointment rides along for the confirmation screen.
  | { type: "CONFIRM_BOOKING"; preferences: BookingPreferences; slot: SelectedSlot; appointment: BookedAppointment }

export const bookingFlowMachine = setup({
  types: {
    input: {} as BookingFlowInput,
    context: {} as BookingFlowContext,
    events: {} as BookingFlowEvent
  },
  guards: {
    insuranceEntry: ({ context }) => context.insuranceEntry,
    isIneligible: ({ context }) => context.exclusionMessageId != null,
    // Skip the consent page when the member has already signed the current required
    // consents — seeded by getConsentStatus and updated after successful consent submission.
    alreadyConsented: ({ context }) => context.alreadySignedConsent,
    hasScreeningQuestions: ({ context }) => context.hasScreeningQuestions,
    needsScreening: ({ context }) => context.hasScreeningQuestions && !context.hasPassedScreening,
    disqualifiedByScreener: ({ context }) => context.disqualifiedByScreener,
    collectsPayment: ({ context }) => context.payment !== null
  },
  actions: {
    clearInsuranceResult: assign({
      insuranceResult: null,
      insuranceError: null,
      insuranceRequestId: null,
      paymentMethod: null
    }),
    recordInsuranceResult: assign({
      insuranceResult: ({ event }) => (event.type === "INSURANCE_RESULT" ? event.result : null),
      insuranceRequestId: null
    }),
    // Record the disqualification's message and its source for the not-eligible page. Only a screener
    // refusal sets disqualifiedByScreener → gating whether "back to questions" is offered (a
    // booking-time known-data refusal must not reopen the gate).
    recordExclusion: assign({
      hasPassedScreening: false,
      exclusionMessageId: ({ event }) => (event.type === "DISQUALIFIED" ? event.exclusionMessageId : null),
      disqualifiedByScreener: ({ event }) => (event.type === "DISQUALIFIED" ? event.fromScreener : false)
    }),
    prepareBooking: assign(({ context, event }) => {
      if (event.type !== "PREPARE_BOOKING") return {}
      const sameBooking = context.bookingId === event.bookingId && context.bookingOfferingId === event.offeringId
      return {
        bookingId: event.bookingId,
        bookingOfferingId: event.offeringId,
        paymentPreauthorized: sameBooking && context.paymentPreauthorized,
        payment: sameBooking ? context.payment : null
      }
    }),
    recordPaymentSelection: assign({
      recovery: null,
      payment: ({ event }) => (event.type === "CONTINUE_TO_PAYMENT" ? event.payment : null),
      paymentMethod: ({ context }) => context.paymentMethod ?? "self_pay",
      preferences: ({ event }) => (event.type === "CONTINUE_TO_PAYMENT" ? event.preferences : null),
      selectedSlot: ({ event }) => (event.type === "CONTINUE_TO_PAYMENT" ? event.slot : null)
    }),
    recordPaymentPreauthorization: assign({ paymentPreauthorized: true }),
    // A confirmed cancellation or abandonment invalidates the attempt; uncertain results must reuse it.
    clearBookingSession: assign({
      bookingId: null,
      bookingOfferingId: null,
      paymentPreauthorized: false
    }),
    clearPaymentSelection: assign({
      recovery: null,
      preferences: null,
      selectedSlot: null,
      payment: null,
      paymentPreauthorized: false
    }),
    clearBookingForFreshStart: assign({
      bookingId: null,
      bookingOfferingId: null,
      payment: null,
      paymentPreauthorized: false
    }),
    clearPaymentAuthorization: assign({
      paymentPreauthorized: false,
      paymentFormRemountKey: ({ context }) => context.paymentFormRemountKey + 1
    }),
    recordRecovery: assign(({ context, event }) => {
      if (event.type !== "BOOKING_RECOVERY") return {}
      return {
        recovery: event.recovery,
        preferences: event.preferences ?? context.preferences,
        selectedSlot: event.slot ?? context.selectedSlot,
        payment: event.payment ?? context.payment
      }
    }),
    recordBooking: assign({
      preferences: ({ event }) => (event.type === "CONFIRM_BOOKING" ? event.preferences : null),
      selectedSlot: ({ event }) => (event.type === "CONFIRM_BOOKING" ? event.slot : null),
      appointment: ({ event }) => (event.type === "CONFIRM_BOOKING" ? event.appointment : null)
    }),
    // Clear the skip flag so the consent state doesn't bounce a member who started signed past it.
    markConsentUnsigned: assign({ alreadySignedConsent: false }),
    markConsentSigned: assign({ alreadySignedConsent: true }),
    markScreeningPassed: assign({ hasPassedScreening: true, exclusionMessageId: null, disqualifiedByScreener: false })
  }
}).createMachine({
  id: "bookingFlow",
  // The known-data eligibility gate runs first (MEM-760): an ineligible member never reaches consent.
  initial: "eligibilityGate",
  context: ({ input }) => ({
    insuranceInputs: null,
    insuranceResult: null,
    insuranceError: null,
    insuranceRequestId: null,
    paymentOptions: null,
    insuranceEntry: input.insuranceEntry === true,
    alreadySignedConsent: input.alreadySignedConsent,
    program: input.program,
    appointmentType: input.appointmentType,
    // Seed from the known-data gate: non-null here means the eligibilityGate routes to notEligible.
    exclusionMessageId: input.knownDataExclusionMessageId,
    hasScreeningQuestions: input.hasScreeningQuestions,
    hasPassedScreening: false,
    disqualifiedByScreener: false,
    preferences: null,
    selectedSlot: null,
    bookingId: null,
    bookingOfferingId: null,
    paymentPreauthorized: false,
    payment: null,
    paymentMethod: null,
    appointment: null,
    recovery: null,
    paymentFormRemountKey: 0
  }),
  on: {
    BOOKING_RECOVERY: [
      {
        target: ".confirmingBooking",
        guard: ({ event }) => event.recovery.action === "pending",
        actions: "recordRecovery"
      },
      {
        target: ".payment",
        guard: and(["collectsPayment", ({ event }) => event.recovery.action === "retry"]),
        actions: "recordRecovery"
      },
      {
        target: ".payment",
        guard: ({ context, event }) =>
          event.recovery.action === "update_payment" && (event.payment ?? context.payment) !== null,
        actions: ["recordRecovery", "clearPaymentAuthorization"]
      },
      {
        target: ".chooseNewSlot",
        guard: ({ event }) => event.recovery.action === "choose_new_slot",
        actions: "recordRecovery"
      },
      {
        target: ".chooseNewSlot",
        guard: ({ event }) => event.recovery.action === "start_over" && event.recovery.restartSafe,
        actions: ["recordRecovery", "clearBookingForFreshStart"]
      },
      {
        target: ".confirmingBooking",
        guard: ({ event }) => event.recovery.action === "retry",
        actions: "recordRecovery"
      },
      { target: ".bookingUnresolved", actions: "recordRecovery" }
    ]
  },
  states: {
    eligibilityGate: {
      always: [
        { target: "notEligible", guard: "isIneligible" },
        { target: "insurance", guard: "insuranceEntry" },
        { target: "consent" }
      ]
    },

    // Insurance coverage checking is a distinct booking phase. Its steps share check initiation,
    // result invalidation, and navigation back to insurance details.
    insurance: {
      id: "insurance",
      on: {
        PAYMENT_OPTIONS_LOADED: [
          {
            target: ".details",
            guard: ({ context, event }) =>
              JSON.stringify(context.paymentOptions?.insurance) !== JSON.stringify(event.paymentOptions?.insurance),
            actions: ["clearInsuranceResult", assign({ paymentOptions: ({ event }) => event.paymentOptions })]
          },
          { actions: assign({ paymentOptions: ({ event }) => event.paymentOptions }) }
        ],
        INSURANCE_INVALIDATED: { target: ".details", actions: "clearInsuranceResult" },
        INSURANCE_BACK: { target: ".details", actions: "clearInsuranceResult" },
        INSURANCE_STARTED: {
          target: ".checking",
          actions: assign({
            insuranceInputs: ({ event }) => event.inputs,
            insuranceRequestId: ({ event }) => event.requestId,
            insuranceResult: null,
            insuranceError: null,
            paymentMethod: null
          })
        }
      },
      initial: "details",
      states: {
        details: {},
        checking: {
          on: {
            INSURANCE_RESULT: [
              {
                target: "covered",
                guard: ({ context, event }) =>
                  context.insuranceRequestId === event.requestId && insuranceResultStep(event.result) === "covered",
                actions: "recordInsuranceResult"
              },
              {
                target: "notCovered",
                guard: ({ context, event }) =>
                  context.insuranceRequestId === event.requestId && insuranceResultStep(event.result) === "notCovered",
                actions: "recordInsuranceResult"
              },
              {
                target: "noProviders",
                guard: ({ context, event }) =>
                  context.insuranceRequestId === event.requestId && insuranceResultStep(event.result) === "noProviders",
                actions: "recordInsuranceResult"
              }
            ],
            INSURANCE_FAILED: {
              target: "error",
              guard: ({ context, event }) => context.insuranceRequestId === event.requestId,
              actions: assign({ insuranceError: ({ event }) => event.error, insuranceRequestId: null })
            }
          }
        },
        error: {},
        covered: { on: { CONTINUE: { target: "#consent", actions: assign({ paymentMethod: "insurance" }) } } },
        // Testing: allow self-pay continuation even when payment options report it unavailable.
        // Follow up with the unavailable-self-pay UX and restore the availability guard before release.
        notCovered: { on: { CONTINUE: "#consent" } },
        noProviders: { on: { CONTINUE: "#consent" } }
      }
    },
    consent: {
      id: "consent",
      always: [{ target: "postConsent", guard: "alreadyConsented" }],
      on: { CONTINUE: { target: "postConsent", actions: "markConsentSigned" } }
    },

    // Transient router (no view): once consent is settled, go to the health-questions step only if
    // this offering has unanswered questions. A successful screening survives an insurance recheck.
    postConsent: {
      always: [{ target: "healthQuestions", guard: "needsScreening" }, { target: "seeAvailability" }]
    },

    // Nested so it can grow into multiple (possibly member-data-driven) views without touching
    // the outer states. The page sends DISQUALIFIED on an ineligible result, else CONTINUE.
    healthQuestions: {
      id: "healthQuestions",
      // Explicitly revisiting the questions requires another successful submission.
      entry: assign({ hasPassedScreening: false }),
      initial: "view",
      states: {
        view: {
          on: {
            CONTINUE: { target: "done", actions: "markScreeningPassed" },
            DISQUALIFIED: { target: "#notEligible", actions: "recordExclusion" }
          }
        },
        done: { type: "final" }
      },
      onDone: { target: "seeAvailability" }
    },

    // Nested so the availability journey can grow without touching the outer states. The
    // preferences view now also hosts the date + time picker (MEM-716): the member sets
    // visit type + location, picks a slot from availability, and confirms — recording the
    // preferences and chosen slot. Empty availability is handled inline on that screen (a
    // no-slots tile), so there's no terminal no-availability state.
    seeAvailability: {
      on: {
        PAYMENT_REQUIREMENT_LOADED: {
          actions: assign({
            paymentMethod: ({ context, event }) => (event.requiresPayment ? context.paymentMethod ?? "self_pay" : null)
          })
        },
        BACK_TO_INSURANCE: {
          target: "#insurance.details",
          guard: ({ context }) => context.insuranceEntry && context.insuranceInputs !== null,
          actions: ["clearInsuranceResult", "clearPaymentSelection"]
        }
      },
      initial: "preferences",
      states: {
        preferences: {
          on: {
            PREPARE_BOOKING: { actions: "prepareBooking" },
            BOOKING_ABANDONED: { actions: ["clearBookingSession", "clearPaymentSelection"] },
            CONTINUE_TO_PAYMENT: { target: "#payment", actions: "recordPaymentSelection" },
            CONFIRM_BOOKING: { target: "done", actions: "recordBooking" },
            // Back to the questions step (MEM-825). Guarded on hasScreeningQuestions so it's a no-op
            // when there were none — the preferences screen backs out of the flow itself in that case.
            BACK_TO_QUESTIONS: { target: "#healthQuestions", guard: "hasScreeningQuestions" },
            // Booking-time eligibility rejection (MEM-771): terminal, routed out like a screening disqualification.
            DISQUALIFIED: { target: "#notEligible", actions: "recordExclusion" },
            // Booking blocked on unsigned consent: back to consent (member re-selects a slot afterward).
            NEEDS_CONSENT: { target: "#consent", actions: "markConsentUnsigned" }
          }
        },
        done: { type: "final" }
      },
      onDone: { target: "photoId" }
    },

    // Keep the authorized payment and booking identity while booking completion is retried.
    payment: {
      id: "payment",
      on: {
        PAYMENT_PREAUTHORIZED: { actions: "recordPaymentPreauthorization" },
        PAYMENT_CANCELED: { actions: "clearBookingSession" },
        BACK_TO_AVAILABILITY: { target: "seeAvailability", actions: "clearPaymentSelection" },
        CONFIRM_BOOKING: { target: "photoId", actions: "recordBooking" },
        DISQUALIFIED: { target: "notEligible", actions: "recordExclusion" },
        NEEDS_CONSENT: { target: "paymentConsent", actions: "markConsentUnsigned" }
      }
    },
    paymentConsent: {
      on: {
        CONTINUE: [
          { target: "payment", guard: "collectsPayment", actions: "markConsentSigned" },
          { target: "confirmingBooking", actions: "markConsentSigned" }
        ]
      }
    },
    confirmingBooking: {
      id: "confirmingBooking",
      on: {
        CONFIRM_BOOKING: { target: "photoId", actions: "recordBooking" },
        PAYMENT_CANCELED: { actions: "clearBookingSession" },
        BACK_TO_AVAILABILITY: { target: "seeAvailability", actions: "clearPaymentSelection" },
        DISQUALIFIED: { target: "notEligible", actions: "recordExclusion" },
        NEEDS_CONSENT: { target: "paymentConsent", actions: "markConsentUnsigned" }
      }
    },
    chooseNewSlot: {
      id: "chooseNewSlot",
      on: {
        CONTINUE: "seeAvailability",
        BACK_TO_QUESTIONS: { target: "healthQuestions", guard: "hasScreeningQuestions" }
      }
    },
    bookingUnresolved: { id: "bookingUnresolved", type: "final" },
    // Booking has already succeeded. This step can only advance to confirmation; it never rebooks.
    photoId: { on: { CONTINUE: "booked" } },
    booked: { type: "final" },
    // Not final: a screener-disqualified member can go back to re-answer (guarded, so a known-data
    // refusal — which never sets disqualifiedByScreener — can't use this to bypass the gate).
    notEligible: {
      id: "notEligible",
      on: { BACK_TO_QUESTIONS: { target: "healthQuestions", guard: "disqualifiedByScreener" } }
    }
  }
})
